import { useEffect, useState } from 'react';
import { Divider } from '@mui/material';
import { Icon, IconTooltip } from '@ts/storybook';
import WrapperEntitySelector from '@modules/entitySelector/WrapperEntitySelector';

import { RadioButtonGroup } from '@components/Inputs';
import EGRActionSelector from './EGRActionSelector';
import StatusChange from './EGRStatusChange';
import { PaperSectionTitle, FlexRow, FlexRowFlexItem, Loading, NewPaperWithPadding } from '@components/Misc';
import Alert from '@mui/material/Alert';
import { generateSearchEntitySelector } from '@modules/entitySelector/entitySelector.utils';
import { useAppSelector } from '@redux/store';
import { Grid, TextInput, Typography, Checkbox } from '@ts/storybook';
import { useTranslation } from 'react-i18next';
import EGRNoStatusChange from './EGRNoStatusChange/EGRNoStatusChange';
import { CEGR, CEGRChangeStatus, CEGRNoChangeStatus, IForm } from '@ts/ui-models';
import { IAppProps } from '@interfaces/App.interface';
import EGRStatusSelection from './EGRStatusSelection';

interface IEGRFormProps extends IAppProps, IForm {
  values: CEGR | CEGRChangeStatus | CEGRNoChangeStatus;
}

const EGRForm = (props: IEGRFormProps) => {
  const { t } = useTranslation();
  const { onCallbackChange, values: EGR } = props;

  const [state, setState] = useState({ behaviourChangeLocation: 0 });
  const readingTypes = useAppSelector(state => state.global.readingTypesList);

  const isReadOnly = props.mode == 'view' || EGR.state === 'initializing';

  //#region Handlers
  const handleChangeAction = action => {
    if (!actionIsTTEGR(action)) {
      resetTimeoutConfig();
    }
    onCallbackChange('actionLogicalId')(action);
    if (action === 'noChangeStatus' && !EGR.actionConfig?.noChangeStatusTriggerField) {
      props.onChange('actionConfig.noChangeStatusTriggerField', 'lastChange');
    }
  };

  const onChangeEntitySelector = (entity, entityKey) => {
    const fkValue = entity ? entity.getID() : null;
    onCallbackChange(entityKey + 'Id')(fkValue);
    onCallbackChange(entityKey)(entity);
  };

  //#endregion

  //#region Functions
  const setbehaviourChangeLocationFromCurValues = () => {
    if (EGR.actionConfig?.currentLocationId) {
      setState({ behaviourChangeLocation: 0 });
    }
    if (EGR.actionConfig?.previousLocationId) {
      setState({ behaviourChangeLocation: 1 });
    }
    if (EGR.actionConfig?.currentLocationId && EGR.actionConfig?.previousLocationId) {
      setState({ behaviourChangeLocation: 2 });
    }
  };

  const resetTimeoutConfig = () => {
    onCallbackChange('timeoutConfig')({
      deadlineTriggerDelay: null,
      noEventRecordedTypeId: null,
    });
  };

  const actionIsGeofencing = action => {
    return action == 'presence' || action == 'enterZone' || action == 'exitZone';
  };

  const actionIsTTEGR = action => {
    return action == 'noChangeStatus' || action == 'noChangeLocation' || action == 'eventAfterEvent';
  };
  //#endregion

  //#region Renders
  const renderTTEGR = () => {
    const { actionLogicalId } = EGR;

    switch (actionLogicalId) {
      case 'noChangeStatus':
        return (
          <FlexRow>
            <FlexRowFlexItem>{renderNoChange('Period no change', true)}</FlexRowFlexItem>
          </FlexRow>
        );
      /* case 'noChangeLocation':
        return (
          <FlexRow>
            <FlexRowFlexItem>{_renderNoChange('of no location change, record an event')}</FlexRowFlexItem>
          </FlexRow>
        );
      case 'eventAfterEvent':
        return (
          <FlexRow>
            <FlexRowFlexItem>
              {_renderNoChange('minutes since the recording of the selected event type, record an event', false, true)}
            </FlexRowFlexItem>
          </FlexRow>
        ); */
    }
  };

  const renderDeviceTypeSelection = () => {
    if (!EGR.actionLogicalId) return null;

    return (
      <Grid container size={{ xs: 12, md: 6 }} direction={'column'} spacing={2} sx={{ alignItems: 'flex-start' }}>
        <PaperSectionTitle>{t('Selection of device type')}</PaperSectionTitle>

        <WrapperEntitySelector
          label="Select device types"
          value={EGR.selectedDeviceTypes}
          onChange={onCallbackChange('selectedDeviceTypes')}
          searchComponent={generateSearchEntitySelector('deviceType')}
          multiple
          isReadOnly={isReadOnly}
        />

        <WrapperEntitySelector
          label="Select labels for the device types"
          value={EGR.selectedDeviceLabels}
          onChange={onCallbackChange('selectedDeviceLabels')}
          searchComponent={generateSearchEntitySelector('label')}
          multiple
          isReadOnly={isReadOnly}
        />
      </Grid>
    );
  };

  const renderItemsSection = () => {
    if (!EGR.actionLogicalId) return null;

    return (
      <Grid container direction={'column'} spacing={2} size={{ xs: 12, md: 6 }} sx={{ alignItems: 'flex-start' }}>
        <PaperSectionTitle>{t('Affected items selection')}</PaperSectionTitle>

        <WrapperEntitySelector
          label="Select item"
          value={EGR.selectedItems}
          onChange={onCallbackChange('selectedItems')}
          searchComponent={generateSearchEntitySelector('item')}
          multiple
          isReadOnly={isReadOnly}
        />
        <WrapperEntitySelector
          label="Select item types"
          value={EGR.selectedItemTypes}
          onChange={onCallbackChange('selectedItemTypes')}
          searchComponent={generateSearchEntitySelector('itemType')}
          multiple
          isReadOnly={isReadOnly}
        />
        <WrapperEntitySelector
          label="Select labels for the items"
          value={EGR.selectedElementLabels}
          onChange={onCallbackChange('selectedElementLabels')}
          searchComponent={generateSearchEntitySelector('label')}
          multiple
          isReadOnly={isReadOnly}
        />
        {<EGRStatusSelection {...props} />}
      </Grid>
    );
  };

  const renderLocationsSelection = () => {
    if (!actionIsGeofencing(EGR.actionLogicalId)) return;

    return (
      <Grid container spacing={2} size={{ xs: 12 }}>
        <Grid size={{ xs: 12 }}>
          <PaperSectionTitle>{t('Selection of locations')}</PaperSectionTitle>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <WrapperEntitySelector
            label="Select locations"
            value={EGR.selectedLocations}
            onChange={onCallbackChange('selectedLocations')}
            searchComponent={generateSearchEntitySelector('location')}
            multiple
            isReadOnly={isReadOnly}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <WrapperEntitySelector
            label="Select type of locations"
            value={EGR.selectedLocationTypes}
            onChange={onCallbackChange('selectedLocationTypes')}
            searchComponent={generateSearchEntitySelector('locationType')}
            multiple
            isReadOnly={isReadOnly}
          />
        </Grid>
      </Grid>
    );
  };

  const renderMain = () => {
    return (
      <Grid container size={{ xs: 12, md: 10 }} spacing={2}>
        <Grid size={{ xs: 12 }}>{renderAlert()}</Grid>
        {EGR.hasWarning && (
          <Grid container size={'auto'} sx={{ alignItems: 'center' }}>
            <IconTooltip text={'Rule Overlap Warning'} tooltipType={'warning'} color="warning" />
          </Grid>
        )}
        <Grid size={4}>
          <TextInput
            isReadOnly={isReadOnly}
            label="Name"
            value={EGR.name}
            onChangeValue={onCallbackChange('name')}
            required={props.requiredFields.includes('name')}
            infoText={!isReadOnly ? 'Blank spaces at the beggining and the end will be trimmed on save' : ''}
          />
        </Grid>

        {((isReadOnly && EGR.notes) || !isReadOnly) && (
          <Grid size={{ xs: 12 }}>
            <TextInput
              isReadOnly={isReadOnly}
              label="Notes"
              value={EGR.notes}
              multiline
              onChangeValue={onCallbackChange('notes')}
              required={props.requiredFields.includes('notes')}
            />
          </Grid>
        )}
        <Grid size={{ xs: 12 }}>
          <WrapperEntitySelector
            searchComponent={generateSearchEntitySelector('eventType')}
            value={EGR.eventType}
            onChangeEntity={eventType => onChangeEntitySelector(eventType, 'eventType')}
            label={'Event to be recorded'}
            isReadOnly={isReadOnly}
            required={props.requiredFields.includes('eventTypeId')}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Checkbox
            label="Enabled"
            isReadOnly={isReadOnly}
            checked={EGR.state === 'enabled'}
            onChangeValue={newValue => {
              const newState = newValue == false ? 'disabled' : 'enabled';
              onCallbackChange('state')(newState);
            }}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Typography variant="h6" text="Action definition" />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <EGRActionSelector
            requiredFields={props.requiredFields}
            value={EGR.actionLogicalId}
            onChange={handleChangeAction}
            disabled={isReadOnly}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>{renderActionConfig()}</Grid>
      </Grid>
    );
  };

  const renderAlert = () => {
    let warningStr;
    if (EGR.state == 'initializing') warningStr = `${t('Locked')}, ${t('definition in progress')}...`;
    else if (props.mode == 'edit') warningStr = t('This EGR will be disabled during editing');
    else return;

    return (
      <div>
        <Alert severity="warning">{warningStr}</Alert>
        <Divider />
      </div>
    );
  };

  const renderChangeLocationAction = () => {
    const { actionConfig } = EGR;

    return (
      <Grid container spacing={1} mt={1} justifyContent="space-around" alignItems="center">
        <Grid size={{ xs: 12 }}>
          <RadioButtonGroup
            title="Behaviour when changing location"
            value={state.behaviourChangeLocation}
            onChange={v => {
              onCallbackChange('actionConfig')({
                previousLocationId: null,
                previousLocation: null,
                currentLocationId: null,
                currentLocation: null,
              });
              setTimeout(() => {
                setState({ behaviourChangeLocation: Number(v) });
              }, 100);
            }}
            disabled={isReadOnly}
            options={[
              {
                label:
                  'When an item is assigned to a particular location, the event will be triggered without considering the previous location.',
                value: 0,
              },
              {
                label:
                  'When an item is disassociated from the location, the event will be triggered without considering the new current location.',
                value: 1,
              },
              {
                label:
                  'When the item transitions from one specific location to another, the event will be triggered, indicating a change in its whereabouts.',
                value: 2,
              },
            ]}
          />
        </Grid>
        {state.behaviourChangeLocation === 1 || state.behaviourChangeLocation === 2 ? (
          <Grid size={{ xs: state.behaviourChangeLocation === 2 ? 5 : 12 }}>
            <WrapperEntitySelector
              label="Previous location"
              value={actionConfig?.previousLocation}
              onChangeEntity={v => {
                {
                  const entity = !Array.isArray(v) ? v : null;
                  onCallbackChange('actionLogicalId')(EGR.actionLogicalId);
                  onCallbackChange('actionConfig')({
                    previousLocationId: entity?.getID() ?? null,
                    previousLocation: entity,
                    currentLocationId: state.behaviourChangeLocation === 2 ? actionConfig?.currentLocationId : null,
                    currentLocation: state.behaviourChangeLocation === 2 ? actionConfig?.currentLocation : null,
                  });
                }
              }}
              searchComponent={generateSearchEntitySelector('location')}
              isReadOnly={isReadOnly}
            />
          </Grid>
        ) : null}
        {state.behaviourChangeLocation === 2 && (
          <Grid size={{ xs: 'auto' }}>
            <Icon icon="arrow_forward" />
          </Grid>
        )}
        {state.behaviourChangeLocation === 0 || state.behaviourChangeLocation === 2 ? (
          <Grid size={{ xs: state.behaviourChangeLocation === 2 ? 5 : 12 }}>
            <WrapperEntitySelector
              label="Current location"
              value={actionConfig?.currentLocation}
              onChangeEntity={v => {
                {
                  const entity = !Array.isArray(v) ? v : null;
                  onCallbackChange('actionLogicalId')(EGR.actionLogicalId);
                  onCallbackChange('actionConfig')({
                    currentLocationId: entity?.getID() ?? null,
                    currentLocation: entity,
                    previousLocationId: state.behaviourChangeLocation === 2 ? actionConfig?.previousLocationId : null,
                    previousLocation: state.behaviourChangeLocation === 2 ? actionConfig?.previousLocation : null,
                  });
                }
              }}
              searchComponent={generateSearchEntitySelector('location')}
              isReadOnly={isReadOnly}
            />
          </Grid>
        ) : null}
      </Grid>
    );
  };

  const renderActionConfig = () => {
    const action = EGR.actionLogicalId;
    if (!action) return;

    if (action === 'selectedReading') {
      return (
        <WrapperEntitySelector
          label="Select a reading type"
          searchComponent={generateSearchEntitySelector('readingType')}
          multiple
          value={EGR.selectedReadingTypes}
          onChange={onCallbackChange('selectedReadingTypes')}
          isReadOnly={isReadOnly}
        />
      );
    }
    if (action === 'changeStatus')
      return (
        <NewPaperWithPadding>
          <StatusChange {...props} values={EGR as CEGRChangeStatus} />
        </NewPaperWithPadding>
      );

    if (action == 'changeLocation') return <NewPaperWithPadding>{renderChangeLocationAction()}</NewPaperWithPadding>;
    else if (actionIsTTEGR(action)) return <NewPaperWithPadding>{renderTTEGR()}</NewPaperWithPadding>;
    else return;
  };

  const renderSelectors = () => {
    return (
      <Grid container size={{ xs: 12, md: 10 }} spacing={4}>
        {renderItemsSection()}
        {renderDeviceTypeSelection()}
        {renderLocationsSelection()}
      </Grid>
    );
  };

  const renderNoChange = (text, withStatusIndicator?) => {
    return <EGRNoStatusChange {...props} text={text} withStatusIndicator={withStatusIndicator} />;
  };

  //#endregion

  useEffect(() => {
    setbehaviourChangeLocationFromCurValues();
  }, []);

  if (!readingTypes) return <Loading />;
  return (
    <Grid container spacing={2} mb={4} justifyContent="center">
      {renderMain()}
      {renderSelectors()}
    </Grid>
  );
};

export default EGRForm;
